import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Flex,
  H4,
  Button,
  Notification,
  Icon,
  Popover,
} from "orcs-design-system";
import Cropper from "react-easy-crop";
import Preview from "./Preview";
import {
  StyledImageEditorContainer,
  ZoomControlsContainer,
  ZoomControlButton,
} from "./styled.components";
import {
  blobToBase64,
  extractBase64String,
  getCroppedImg,
  calculateOptimalZoom,
} from "./utils/canvasUtils";
import { AVATAR_MODAL_CROP_PX } from "./const";

const EditMode = ({
  initialCroppedArea,
  onSave,
  onCancel,
  imageSrc,
  isSaving,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedArea, setCroppedArea] = useState(null);
  const [error, setError] = useState(null);

  const onCropComplete = useCallback((cropArea, cropAreaPixels) => {
    setCroppedAreaPixels(cropAreaPixels);
    setCroppedArea(cropArea);
  }, []);

  /** Must set both args — `onCropAreaChange` passes (area, areaPixels); using `setCroppedArea` alone dropped pixels and broke Save. */
  const onCropAreaChange = useCallback((cropArea, cropAreaPixels) => {
    setCroppedArea(cropArea);
    setCroppedAreaPixels(cropAreaPixels);
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoom((prevZoom) => Math.min(prevZoom + 0.5, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prevZoom) => Math.max(prevZoom - 0.5, 0.5));
  }, []);

  const handleCenterImage = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  const onSaveImage = useCallback(async () => {
    try {
      if (
        !croppedAreaPixels ||
        typeof croppedAreaPixels.width !== "number" ||
        typeof croppedAreaPixels.height !== "number"
      ) {
        setError(
          new Error(
            "Crop is not ready yet. Adjust the image slightly and try again."
          )
        );
        return;
      }
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      if (!croppedImageBlob || croppedImageBlob instanceof Error) {
        setError(
          croppedImageBlob instanceof Error
            ? croppedImageBlob
            : new Error("Could not crop image.")
        );
        return;
      }
      const croppedImageBase64 = await blobToBase64(croppedImageBlob);
      const croppedImage = extractBase64String(croppedImageBase64);
      const originalImage = imageSrc.startsWith("data:")
        ? extractBase64String(imageSrc)
        : undefined;

      onSave({ originalImage, croppedImage, croppedArea });
    } catch (e) {
      setError(
        e instanceof Error ? e : new Error("Could not save profile image")
      );
    }
  }, [onSave, imageSrc, croppedArea, croppedAreaPixels]);

  const cropShape = "round";

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      height="100%"
      width="100%"
      gap="10px"
    >
      <StyledImageEditorContainer>
        {isSaving ? (
          <Preview
            croppedArea={croppedArea}
            imageSrc={imageSrc}
            cropShape={cropShape}
            width={`${AVATAR_MODAL_CROP_PX}px`}
            borderSize="6px"
          />
        ) : (
          <Cropper
            image={imageSrc}
            initialCroppedAreaPercentages={initialCroppedArea}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape={cropShape}
            cropSize={{
              height: AVATAR_MODAL_CROP_PX,
              width: AVATAR_MODAL_CROP_PX,
            }}
            showGrid={false}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            onCropAreaChange={onCropAreaChange}
            restrictPosition={false}
            minZoom={0.5} // Allow smaller zoom for very large images
            maxZoom={5} // Increased max zoom for better control
            objectFit="contain"
            onMediaLoaded={(mediaSize) => {
              if (!initialCroppedArea) {
                // Use the new calculateOptimalZoom function for better initial zoom
                const optimalZoom = calculateOptimalZoom(
                  mediaSize,
                  AVATAR_MODAL_CROP_PX
                );
                setZoom(optimalZoom);
              }
            }}
          />
        )}
      </StyledImageEditorContainer>

      <Flex flexDirection="column" alignItems="center" gap="10px">
        <Flex
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          width="100%"
          position="relative"
        >
          <H4 style={{ position: "absolute", left: 0 }}>Preview</H4>
          <ZoomControlsContainer>
            <Popover
              text="Zoom out - Make the image smaller"
              direction="top"
              width="fit-content"
            >
              <ZoomControlButton
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                aria-label="Zoom out"
              >
                <Icon icon={["fas", "minus"]} />
              </ZoomControlButton>
            </Popover>
            <Popover
              text="Center image - Reset position and zoom"
              direction="top"
              width="fit-content"
            >
              <ZoomControlButton
                onClick={handleCenterImage}
                aria-label="Center image"
              >
                <Icon icon={["fas", "expand-arrows-alt"]} />
              </ZoomControlButton>
            </Popover>
            <Popover
              text="Zoom in - Make the image larger"
              direction="top"
              width="fit-content"
            >
              <ZoomControlButton
                onClick={handleZoomIn}
                disabled={zoom >= 5}
                aria-label="Zoom in"
              >
                <Icon icon={["fas", "plus"]} />
              </ZoomControlButton>
            </Popover>
          </ZoomControlsContainer>
        </Flex>
        <Flex flexDirection="row" alignItems="center" gap="10px">
          <>
            <Preview
              croppedArea={croppedArea}
              imageSrc={imageSrc}
              cropShape={cropShape}
              width="150px"
              borderSize="6px"
            />
            <Preview
              croppedArea={croppedArea}
              imageSrc={imageSrc}
              cropShape={cropShape}
              width="65px"
              borderSize="4px"
            />
          </>
        </Flex>
      </Flex>

      {error && (
        <Notification variant="error" closable={false}>
          {error instanceof Error ? error.message : String(error)}
        </Notification>
      )}

      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-end"
        gap="10px"
        width="100%"
      >
        <Button
          disabled={isSaving}
          ariaLabel="Save"
          variant="success"
          onClick={onSaveImage}
        >
          Save
        </Button>

        <Button
          disabled={isSaving}
          ariaLabel="Cancel"
          variant="ghost"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </Flex>
    </Flex>
  );
};

EditMode.propTypes = {
  initialCroppedArea: PropTypes.object,
  imageSrc: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
};

export default EditMode;
